// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Relative-time label for "Updated: X ago" indicators.
//
// Tier boundaries match the existing Optimizer GlobalToolbar
// (src/screens/schedule/GlobalToolbar.js ~line 140) so all "X ago" labels
// in the app round consistently. Returns a string suitable for direct
// display; callers pair this with a setInterval ticker to refresh.
//
// Pure — accepts the "when" timestamp + an optional "now" timestamp so
// tests can pin both inputs deterministically.
//
// Returns "" for missing/invalid inputs so the UI can safely render
// `Updated: ${label}` without producing "Updated: null" or "Updated: NaN
// ago" garbage.

/**
 * @param {number|null|undefined} whenMs   — epoch-ms of the data fetch
 * @param {number} [nowMs=Date.now()]       — epoch-ms "now" (override in tests)
 * @returns {string} e.g. "<1 min ago" / "5 min ago" / "2 hr ago" / "3 day ago"
 */
export function timeAgoLabel(whenMs, nowMs = Date.now()) {
  if (
    typeof whenMs !== "number" ||
    !isFinite(whenMs) ||
    whenMs <= 0
  ) {
    return "";
  }
  const diffSec = Math.max(0, Math.floor((nowMs - whenMs) / 1000));
  if (diffSec < 60) return "<1 min ago";
  if (diffSec < 3600) {
    const mins = Math.floor(diffSec / 60);
    return `${mins} min ago`;
  }
  if (diffSec < 86400) {
    const hrs = Math.floor(diffSec / 3600);
    return `${hrs} hr ago`;
  }
  const days = Math.floor(diffSec / 86400);
  return `${days} day ago`;
}

/**
 * Recommended setInterval delay for re-rendering the label, given a
 * timestamp. Mirrors GlobalToolbar's tiered intervals — finer-grain
 * updates when the timestamp is recent, coarser when older.
 *
 * @param {number|null|undefined} whenMs
 * @param {number} [nowMs=Date.now()]
 * @returns {number} ms — pass to setInterval
 */
export function timeAgoTickInterval(whenMs, nowMs = Date.now()) {
  if (
    typeof whenMs !== "number" ||
    !isFinite(whenMs) ||
    whenMs <= 0
  ) {
    return 30_000; // 30s default while data is loading
  }
  const diffSec = Math.max(0, Math.floor((nowMs - whenMs) / 1000));
  if (diffSec < 60) return 5_000; // 5s — about to tick over the "<1 min" boundary
  if (diffSec < 3600) return 30_000; // 30s — minutes resolution
  if (diffSec < 86400) return 5 * 60_000; // 5 min — hours resolution
  return 60 * 60_000; // 1 hr — days resolution
}
